import { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-public";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { formatCurrency } from "@/lib/format";
import { numberFieldProps } from "@/lib/number-field";
import { toast } from "sonner";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { useInvoiceResources, useCurrencyScopedProducts, type TaxRateRow } from "@/hooks/useInvoiceResources";
import { SearchCombobox, type SearchComboboxHit } from "@/components/SearchCombobox";
import { ErrorState, LoadingState } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { cn } from "@/lib/utils";
import {
  DiscountFields,
  computeTotals,
  discountPayload,
  discountValueIssue,
  discountExceedsIssues,
  discountTypeSchema,
  discountValueSchema,
  type DiscountType,
  EarlyPaymentDiscountFields,
  earlyPaymentDiscountIssues,
  earlyPaymentDiscountPayload,
} from "@/components/Discount";
import { EarlyPaymentPreview } from "@/components/CreateInvoicePage";

type InvoiceResponse = components["schemas"]["InvoiceResponse"];
type ProductResponse = components["schemas"]["ProductResponse"];


const editInvoiceSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  currency: z.string().min(1, "Currency is required"),
  memo: z.string().nullable(),
  paymentTermsDays: z
    .number()
    .int("Payment terms must be a whole number of days")
    .min(0, "Payment terms cannot be negative")
    .max(365, "Payment terms cannot exceed 365 days"),
  discountType: discountTypeSchema,
  discountValue: discountValueSchema,
  earlyPaymentDiscountType: discountTypeSchema,
  earlyPaymentDiscountValue: discountValueSchema,
  earlyPaymentDiscountExpiresOn: z.string().nullable(),
  lineItems: z
    .array(
      z.object({
        productId: z.string().min(1, "Product is required"),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        taxRateId: z.string().nullable(),
        discountType: discountTypeSchema,
        discountValue: discountValueSchema,
        // Display-only: lets the picker show a product outside the loaded page of results. Not sent to the API.
        productName: z.string().nullish(),
      }),
    )
    .min(1, "At least one line item is required"),
})
  .superRefine((values, ctx) => {
    const headerIssue = discountValueIssue(values.discountType, values.discountValue);
    if (headerIssue)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["discountValue"], message: headerIssue });

    values.lineItems.forEach((item, index) => {
      const issue = discountValueIssue(item.discountType, item.discountValue);
      if (issue)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "discountValue"],
          message: issue,
        });
    });
  });

type EditInvoiceFormValues = z.infer<typeof editInvoiceSchema>;

function LineItemRow({
  index,
  control,
  products,
  taxRates,
  getProductPrice,
  currency,
  dragIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
  onNewProduct,
  searchProducts,
  onProductPicked,
}: {
  index: number;
  control: ReturnType<typeof useForm<EditInvoiceFormValues>>["control"];
  products: ProductResponse[];
  taxRates: TaxRateRow[];
  getProductPrice: (productId: string) => number;
  currency: string;
  dragIndex: number | null;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onRemove: (index: number) => void;
  onNewProduct: () => void;
  searchProducts: (query: string, currency: string) => Promise<SearchComboboxHit[]>;
  onProductPicked: (index: number, hit: SearchComboboxHit) => void;
}) {
  const selectedProductId = useWatch({ control, name: `lineItems.${index}.productId` });
  const productName = useWatch({ control, name: `lineItems.${index}.productName` });
  const quantity = useWatch({ control, name: `lineItems.${index}.quantity` });
  const discountType = useWatch({ control, name: `lineItems.${index}.discountType` });
  const discountValue = useWatch({ control, name: `lineItems.${index}.discountValue` });
  const unitPrice = getProductPrice(selectedProductId);
  // The invoice-level discount is apportioned across lines, so it belongs in the totals panel, not one row.
  const linePreview = computeTotals([
    { quantity: quantity || 0, unitPrice, discountType, discountValue, taxRatePercent: 0 },
  ]);
  const lineGross = linePreview.subTotal;
  const lineDiscount = linePreview.lineDiscountTotal;
  const lineTotal = lineGross - lineDiscount;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={cn(
        "space-y-3 rounded-lg border bg-card/40 p-4 transition-opacity",
        dragIndex === index && "opacity-50",
      )}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="size-4 text-muted-foreground cursor-grab shrink-0" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Line {index + 1}
        </span>
        <div className="flex-1" />
        {lineGross > 0 && (
          <span className="text-sm font-semibold tabular-nums">
            {lineDiscount > 0 && (
              <span className="mr-2 font-normal text-muted-foreground line-through">
                {formatCurrency(lineGross, currency)}
              </span>
            )}
            {formatCurrency(lineTotal, currency)}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          aria-label={`Remove line ${index + 1}`}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,0.9fr)]">
        <FormField
          control={control}
          name={`lineItems.${index}.productId`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Product</FormLabel>
              <FormControl>
                <SearchCombobox
                  value={itemField.value}
                  selectedLabel={
                    products.find((p) => p.id === itemField.value)?.name ??
                    productName ??
                    undefined
                  }
                  placeholder="Select product"
                  searchPlaceholder="Search products…"
                  emptyText="No products found"
                  search={(q) => searchProducts(q, currency)}
                  fetchOnOpen
                  onSelect={(id, hit) => {
                    itemField.onChange(id);
                    onProductPicked(index, hit);
                  }}
                  createOption={{ label: "New Product", onSelect: onNewProduct }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.taxRateId`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Tax Rate</FormLabel>
              <Select
                onValueChange={(val) => itemField.onChange(val === "__none__" ? null : val)}
                value={itemField.value ?? "__none__"}
              >
                <FormControl>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {taxRates.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      <span>
                        {rate.name} ({rate.rate}%)
                      </span>
                      {rate.source === "country" && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          Country rate
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.quantity`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Quantity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min="0.01"
                  {...itemField}
                  {...numberFieldProps(itemField)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <DiscountFields
        control={control}
        typeName={`lineItems.${index}.discountType`}
        valueName={`lineItems.${index}.discountValue`}
        currency={currency}
        label="Line discount"
        compact
      />

      {selectedProductId && (
        <p className="text-xs text-muted-foreground">
          Unit price: {formatCurrency(unitPrice, currency)}
          {lineDiscount > 0 && `, less ${formatCurrency(lineDiscount, currency)} discount`}
        </p>
      )}
    </div>
  );
}

function InvoiceTotalsCard({
  control,
  getProductPrice,
  taxRates,
  children,
}: {
  control: ReturnType<typeof useForm<EditInvoiceFormValues>>["control"];
  getProductPrice: (productId: string) => number;
  taxRates: TaxRateRow[];
  children?: React.ReactNode;
}) {
  const currency = useWatch({ control, name: "currency" });
  const lineItems = useWatch({ control, name: "lineItems" });
  const discountType = useWatch({ control, name: "discountType" });
  const discountValue = useWatch({ control, name: "discountValue" });
  const paymentTermsDays = useWatch({ control, name: "paymentTermsDays" });
  const earlyPaymentType = useWatch({ control, name: "earlyPaymentDiscountType" });
  const earlyPaymentValue = useWatch({ control, name: "earlyPaymentDiscountValue" });
  const earlyPaymentExpiresOn = useWatch({ control, name: "earlyPaymentDiscountExpiresOn" });

  const totals = computeTotals(
    (lineItems ?? []).map((item) => ({
      quantity: item?.quantity || 0,
      unitPrice: getProductPrice(item?.productId ?? ""),
      discountType: item?.discountType,
      discountValue: item?.discountValue,
      taxRatePercent: Number(taxRates.find((r) => r.id === item?.taxRateId)?.rate ?? 0),
    })),
    discountType,
    discountValue,
  );

  // A line without a product prices at zero, so the total would quietly understate the invoice.
  const unpriced = (lineItems ?? []).filter((item) => !item?.productId).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <DiscountFields
          control={control}
          typeName="discountType"
          valueName="discountValue"
          currency={currency}
          label="Invoice discount"
        />
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Applied after line discounts, before tax.
          <InfoHint label="More about the invoice discount">
            The discount is spread across the lines before tax, so tax is charged on the discounted
            amount.
          </InfoHint>
        </p>

        <dl className="space-y-1.5 border-t pt-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="tabular-nums">{formatCurrency(totals.subTotal, currency)}</dd>
          </div>
          {totals.discountTotal > 0 && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Discount</dt>
              <dd className="tabular-nums">-{formatCurrency(totals.discountTotal, currency)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Tax</dt>
            <dd className="tabular-nums">{formatCurrency(totals.taxAmount, currency)}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t pt-2">
            <dt className="font-semibold">Total</dt>
            <dd className="text-xl font-bold tracking-tight tabular-nums">
              {formatCurrency(totals.totalAmount, currency)}
            </dd>
          </div>
          <EarlyPaymentPreview
            totalAmount={totals.totalAmount}
            currency={currency}
            type={earlyPaymentType}
            value={earlyPaymentValue}
            expiresOn={earlyPaymentExpiresOn}
          />
        </dl>
        <div className="border-t pt-3">
          <EarlyPaymentDiscountFields
            control={control}
            typeName="earlyPaymentDiscountType"
            valueName="earlyPaymentDiscountValue"
            expiresOnName="earlyPaymentDiscountExpiresOn"
            currency={currency}
            paymentTermsDays={paymentTermsDays}
          />
        </div>
        {unpriced > 0 ? (
          <p className="text-xs text-amber-600">
            {unpriced === 1 ? "1 line has" : `${unpriced} lines have`} no product yet, so the total is
            incomplete.
          </p>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

export interface EditInvoicePageProps {
  invoiceId: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

export function EditInvoicePage({ invoiceId, onSaved, onCancel }: EditInvoicePageProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const resources = useInvoiceResources();
  const { customers, products, currencies, taxRates, getProductPrice, upsertProducts } = resources;
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const form = useForm<EditInvoiceFormValues>({
    resolver: zodResolver(editInvoiceSchema),
    defaultValues: {
      customerId: "",
      currency: "",
      memo: "",
      paymentTermsDays: 30,
      discountType: "None",
      discountValue: null,
      earlyPaymentDiscountType: "None",
      earlyPaymentDiscountValue: null,
      earlyPaymentDiscountExpiresOn: null,
      lineItems: [],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });

  // Keeps existing lines on initial hydration; a later currency switch clears now-wrong products.
  useCurrencyScopedProducts(form);

  // Products stay scoped to the invoice's currency; SearchCombobox debounces.
  async function searchCustomers(query: string): Promise<SearchComboboxHit[]> {
    const { data } = await api.GET("/v1/customers", {
      params: { query: { Name: query || undefined, Limit: 10 } },
    });
    return (data?.data ?? []).map((c) => ({
      id: c.id!,
      name: c.name,
      sublabel: c.email,
    }));
  }

  async function searchProducts(query: string, currency: string): Promise<SearchComboboxHit[]> {
    const { data } = await api.GET("/v1/products", {
      params: { query: { Name: query || undefined, Currency: currency || undefined, Limit: 10 } },
    });
    const items = data?.data ?? [];
    // Keep picked products resolvable by getProductPrice even if they weren't in the initial page.
    upsertProducts(items);
    return items.map((p) => ({
      id: p.id!,
      name: p.name,
      sublabel: p.description,
    }));
  }

  useEffect(() => {
    if (!invoiceId) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [invoiceRes, orgArRes] = await Promise.all([
          api.GET("/v1/invoices/{id}", { params: { path: { id: invoiceId! } } }),
          organisationId
            ? api.GET("/v1/organisations/{organisationId}/settings/ar", {
                params: { path: { organisationId } },
              })
            : Promise.resolve(null),
          resources.fetchAll({ includeLists: false }),
        ]);
        // Fallback for legacy invoices whose PaymentTermsDays was never snapshotted (null).
        const orgDefaultTerms = orgArRes?.data?.defaultPaymentTermsDays != null
          ? Number(orgArRes.data.defaultPaymentTermsDays)
          : 30;

        if (invoiceRes.error || !invoiceRes.data) {
          setError("Failed to load invoice");
          return;
        }

        if (invoiceRes.data.status !== "Draft") {
          setError("Only draft invoices can be edited");
          return;
        }

        const inv = invoiceRes.data;
        setInvoice(inv);

        // The catalogue isn't bulk-loaded, so seed getProductPrice and picker labels from the invoice's own lines.
        upsertProducts(
          (inv.lineItems ?? [])
            .filter((li) => li.productId)
            .map((li) => ({
              id: li.productId!,
              name: li.productName ?? "",
              unitPrice: li.unitPrice,
              currency: inv.currency,
            })),
        );

        form.reset({
          customerId: inv.customerId ?? "",
          currency: inv.currency,
          memo: inv.memo ?? "",
          paymentTermsDays: inv.paymentTermsDays != null ? Number(inv.paymentTermsDays) : orgDefaultTerms,
          discountType: (inv.discountType as DiscountType | undefined) ?? "None",
          discountValue: inv.discountValue != null ? Number(inv.discountValue) : null,
          earlyPaymentDiscountType: (inv.earlyPaymentDiscount?.type as DiscountType | undefined) ?? "None",
          earlyPaymentDiscountValue:
            inv.earlyPaymentDiscount?.value != null ? Number(inv.earlyPaymentDiscount.value) : null,
          earlyPaymentDiscountExpiresOn: inv.earlyPaymentDiscount?.expiresOn ?? null,
          lineItems:
            inv.lineItems?.map((item) => ({
              productId: item.productId ?? "",
              quantity: typeof item.quantity === "string" ? parseFloat(item.quantity) : (item.quantity ?? 1),
              taxRateId: item.taxRateId ?? null,
              discountType: (item.discountType as DiscountType | undefined) ?? "None",
              discountValue: item.discountValue != null ? Number(item.discountValue) : null,
              productName: item.productName ?? null,
            })) ?? [],
        });
      } catch (e) {
        console.error("Failed to load data:", e);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [invoiceId]);

  async function onSubmit(values: EditInvoiceFormValues) {
    if (!invoiceId) return;

    // The "discount bigger than what it applies to" rule needs resolved prices, so it's enforced here, before the request.
    const totals = computeTotals(
        values.lineItems.map((item) => ({
          quantity: item.quantity || 0,
          unitPrice: getProductPrice(item.productId),
          discountType: item.discountType,
          discountValue: item.discountValue,
          taxRatePercent: Number(taxRates.find((r) => r.id === item.taxRateId)?.rate ?? 0),
        })),
        values.discountType,
        values.discountValue,
      );
    const exceeded = discountExceedsIssues(totals);
    const firstLineIssue = exceeded.lines.findIndex((issue) => issue !== null);
    if (firstLineIssue !== -1) {
      form.setError(`lineItems.${firstLineIssue}.discountValue`, {
        message: exceeded.lines[firstLineIssue] ?? undefined,
      });
      return;
    }
    if (exceeded.invoice) {
      form.setError("discountValue", { message: exceeded.invoice });
      return;
    }

    // Sent only when touched: the server re-checks the date window whenever it receives the discount, so an
    // unrelated edit to a draft whose discount date has passed still saves (sending it is what catches that).
    const dirty = form.formState.dirtyFields;
    const earlyPaymentTouched =
      dirty.earlyPaymentDiscountType || dirty.earlyPaymentDiscountValue || dirty.earlyPaymentDiscountExpiresOn;
    if (earlyPaymentTouched) {
      const issues = earlyPaymentDiscountIssues(
        values.earlyPaymentDiscountType,
        values.earlyPaymentDiscountValue,
        values.earlyPaymentDiscountExpiresOn,
        totals.totalAmount,
        values.paymentTermsDays,
      );
      if (issues.value || issues.expiresOn) {
        if (issues.value) form.setError("earlyPaymentDiscountValue", { message: issues.value });
        if (issues.expiresOn) form.setError("earlyPaymentDiscountExpiresOn", { message: issues.expiresOn });
        return;
      }
    }

    setSaving(true);
    setSaveError(null);

    try {
      const { error: apiError } = await api.PATCH("/v1/invoices/{id}", {
        params: { path: { id: invoiceId } },
        body: {
          customerId: values.customerId,
          currency: values.currency,
          memo: values.memo || null,
          // Only send when changed, so an untouched edit never overwrites the invoice's snapshot.
          paymentTermsDays: form.formState.dirtyFields.paymentTermsDays ? values.paymentTermsDays : undefined,
          ...discountPayload(values.discountType, values.discountValue),
          earlyPaymentDiscount: earlyPaymentTouched
            ? (earlyPaymentDiscountPayload(
                values.earlyPaymentDiscountType,
                values.earlyPaymentDiscountValue,
                values.earlyPaymentDiscountExpiresOn,
              ) ?? { type: "None" })
            : undefined,
          lineItems: values.lineItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            taxRateId: item.taxRateId || null,
            ...discountPayload(item.discountType, item.discountValue),
          })),
        },
      });

      if (apiError) {
        const message = apiErrorMessage(apiError, "Failed to save invoice.");
        setSaveError(message);
        toast.error(message);
        return;
      }

      onSaved(invoiceId);
    } catch (e) {
      console.error("Failed to save invoice:", e);
      setSaveError("An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  }

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    move(dragIndexRef.current, index);
    dragIndexRef.current = index;
  }

  function handleDragEnd() {
    dragIndexRef.current = null;
    setDragIndex(null);
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading invoice..." />
      </PageContainer>
    );
  }

  if (error || !invoice) {
    return (
      <PageContainer>
        <BackLink onClick={onCancel}>Back to invoice</BackLink>
        <ErrorState message={error ? "Couldn't load this invoice." : "That invoice could not be found."} onRetry={error ? () => window.location.reload() : undefined} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink onClick={onCancel}>Back to invoice</BackLink>
      <PageHeader
        eyebrow="Receivables"
        title="Edit invoice"
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2">
            <span className="font-mono">{invoice.documentNumber || "Draft"}</span>
            {invoice.customer?.name ? (
              <>
                <span aria-hidden>·</span>
                <span>{invoice.customer.name}</span>
              </>
            ) : null}
          </span>
        }
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid items-start gap-6 @4xl:grid-cols-[minmax(0,1fr)_20rem]"
        >
          <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Who and when</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2">
                <FormField
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <SearchCombobox
                          value={field.value}
                          selectedLabel={
                            customers.find((c) => c.id === field.value)?.name ??
                            invoice?.customer?.name ??
                            undefined
                          }
                          placeholder="Select customer"
                          searchPlaceholder="Search customers…"
                          emptyText="No customers found"
                          search={searchCustomers}
                          fetchOnOpen
                          onSelect={(id) => field.onChange(id)}
                          createOption={{
                            label: "New Customer",
                            onSelect: () => resources.setNewCustomerOpen(true),
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencies.map((c) => (
                            <SelectItem key={c.id} value={c.code}>
                              {c.code} - {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="A note printed on the invoice"
                        className="resize-none"
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-1.5">
                      <FormLabel>Payment terms (days)</FormLabel>
                      <InfoHint label="More about payment terms">
                        Days until the invoice is due. Starts from your organisation's default; 0 means
                        due on receipt.
                      </InfoHint>
                    </div>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="365"
                        {...field}
                        {...numberFieldProps(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Lines</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({
                    productId: "",
                    quantity: 1,
                    taxRateId: null,
                    discountType: "None",
                    discountValue: null,
                    productName: null,
                  })
                }
              >
                <Plus className="size-4" />
                Add line
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.length === 0 && (
                <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                  No lines yet. Add one to price the invoice.
                </div>
              )}

              {fields.map((field, index) => (
                <LineItemRow
                  key={field.id}
                  index={index}
                  control={form.control}
                  products={products}
                  taxRates={taxRates}
                  getProductPrice={getProductPrice}
                  currency={form.getValues("currency")}
                  dragIndex={dragIndex}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onRemove={remove}
                  onNewProduct={() => resources.setNewProductOpen(true)}
                  searchProducts={searchProducts}
                  onProductPicked={(i, hit) =>
                    form.setValue(`lineItems.${i}.productName`, hit.name)
                  }
                />
              ))}

              {form.formState.errors.lineItems?.root && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lineItems.root.message}
                </p>
              )}
            </CardContent>
          </Card>

          </div>

          {/* Kept in view while the lines are edited. */}
          <aside className="space-y-4 @4xl:sticky @4xl:top-6">
            <InvoiceTotalsCard
              control={form.control}
              getProductPrice={getProductPrice}
              taxRates={taxRates}
            >
              {saveError && (
                <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {saveError}
                </p>
              )}
              <div className="flex flex-col gap-2 border-t pt-4">
                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? <Spinner className="size-4" /> : <Save className="size-4" />}
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </InvoiceTotalsCard>
          </aside>
        </form>
      </Form>

      <CreateCustomerDialog
        open={resources.newCustomerOpen}
        onOpenChange={resources.setNewCustomerOpen}
        onCreated={resources.refreshCustomers}
      />
      <CreateProductDialog
        open={resources.newProductOpen}
        onOpenChange={resources.setNewProductOpen}
        defaultCurrency={form.getValues("currency") || undefined}
        onCreated={() => resources.refreshProducts(form.getValues("currency"))}
      />
    </PageContainer>
  );
}
